import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { agencySessionKey, subscribeAgencySession } from "../../../utils/agencySession.ts";
import AppFeedback from "../../shared/AppFeedback.tsx";
import { addFavorite, getFavorites, removeFavorite } from "./favoriteService.ts";

export function useVariantFavorites() {
  const language = useStoreLanguage();
  const session = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [ready, setReady] = useState(false);
  const locks = useRef(new Set<number>());

  useEffect(function () {
    let active = true;
    setFavoriteIds(new Set());
    setReady(false);
    if (!session) return;
    async function load() {
      try {
        const favorites = await getFavorites();
        if (!active || agencySessionKey() !== session) return;
        setFavoriteIds(new Set(favorites.map(function (favorite) {
          return favorite.productVariantId;
        })));
        setReady(true);
      } catch (error) {
        if (active && agencySessionKey() === session) await AppFeedback.httpError(error);
      }
    }
    void load();
    return function () { active = false; };
  }, [session, language]);

  async function toggle(productVariantId: number) {
    if (!ready || !session || session !== agencySessionKey() || locks.current.has(productVariantId)) return;
    locks.current.add(productVariantId);
    setPendingIds(new Set(locks.current));
    const wasFavorite = favoriteIds.has(productVariantId);
    try {
      if (wasFavorite) {
        await removeFavorite(productVariantId);
      } else {
        await addFavorite(productVariantId);
      }
      if (session !== agencySessionKey()) return;
      setFavoriteIds(function (current) {
        const next = new Set(current);
        if (wasFavorite) next.delete(productVariantId);
        else next.add(productVariantId);
        return next;
      });
    } catch (error) {
      if (session === agencySessionKey()) await AppFeedback.httpError(error);
    } finally {
      locks.current.delete(productVariantId);
      setPendingIds(new Set(locks.current));
    }
  }

  return { favoriteIds, pendingIds, ready, toggle };
}
